// Contracts for Dou Dizhu tables (doudizhu, 斗地主): 3 seats, live players, bots fill empty seats (spec 2026-09-30).
//   GET  /api/doudizhu/lobby → DdzLobbyResponse          GET /api/doudizhu/state → DdzTableView | DdzQueueView | null
//   socket: see DdzClientToServer / DdzServerToClient.
// Hidden information: a seat's cards are sent ONLY in that player's own `ddz:table` (user room), never to the table room;
// other hands appear in `result.hands` once the hand has ended (摊牌).
// Cards: 0…51 as in 炸金花 (face = id >> 2: 0 = '2' … 12 = 'A'; suit = id & 3), 52 = 小王, 53 = 大王.
import { z } from 'zod';
import type { ApiError } from '../errors';

export const DDZ_TIERS = ['novice', 'junior', 'middle', 'senior'] as const;
export type DdzTier = (typeof DDZ_TIERS)[number];
export const zDdzTier = z.enum(DDZ_TIERS);

export type DdzSeat = 0 | 1 | 2;

/** same names and order as DDZ_PATTERN_TYPES in @tg/games/doudizhu (pinned by a server test in 7б-2) */
export const DDZ_PATTERNS = [
  'single', 'pair', 'trio', 'trio1', 'trio2', 'straight', 'pairs', 'plane', 'plane1', 'plane2', 'four2', 'four22', 'bomb', 'rocket',
] as const;
export type DdzPatternName = (typeof DDZ_PATTERNS)[number];

export const zDdzCard = z.number().int().min(0).max(53);
export const zDdzPatternKey = z
  .object({ type: z.enum(DDZ_PATTERNS), rank: z.number().int().min(0).max(14), len: z.number().int().min(1).max(12) })
  .strict();
export const zDdzAction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bid'), bid: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]) }).strict(),
  z.object({ type: z.literal('play'), cards: z.array(zDdzCard).min(1).max(20), as: zDdzPatternKey.optional() }).strict(),
  z.object({ type: z.literal('pass') }).strict(),
]);
export type DdzActionInput = z.infer<typeof zDdzAction>;
/** `seq` = the table view's `seq` the player acted on: a repeat returns the current view, a stale one is rejected */
export const zDdzActionInput = z.object({ handId: z.string().uuid(), seq: z.number().int().min(0).max(100_000), action: zDdzAction }).strict();
export type DdzActionRequest = z.infer<typeof zDdzActionInput>;

/** private table codes: no look-alikes (0/O, 1/I/L) and no «4» */
export const DDZ_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ2356789';
export const zDdzCode = z.string().regex(/^[ABCDEFGHJKMNPQRSTUVWXYZ2356789]{6}$/);
/** the client's contribution to the hand's clientSeed (sent automatically after the hash is shown) */
export const zDdzSaltInput = z.object({ handId: z.string().uuid(), salt: z.string().regex(/^[0-9a-f]{32}$/) }).strict();

export interface DdzPlayedView {
  type: DdzPatternName;
  rank: number;
  len: number;
  cards: number[];
}

export interface DdzSeatView {
  seat: DdzSeat;
  /** null = empty seat */
  player: { publicId: string; nickname: string; avatar: string | null; bot: boolean } | null;
  /** cards in hand */
  cards: number;
  bid: 0 | 1 | 2 | 3 | null;
  role: 'landlord' | 'peasant' | null;
  /** what the seat did in the current trick */
  last: DdzPlayedView | 'pass' | null;
  /** 托管 */
  auto: boolean;
  connected: boolean;
}

export type DdzTablePhase = 'waiting' | 'dealing' | 'bidding' | 'playing' | 'ended';

export interface DdzHandResult {
  landlord: DdzSeat;
  landlordWon: boolean;
  bid: number;
  bombs: number;
  rocket: boolean;
  spring: boolean;
  antiSpring: boolean;
  /** bid × 2^doublings, uncapped */
  multiple: number;
  /** min(multiple, capX) — what is paid */
  capped: number;
  base: number;
  /**
   * per seat: stake = Bet.amount — the reserve placed with the deal, before any card is seen (the most this seat could
   * lose whoever became the landlord); 0 = plays for nothing (bots, points, or the reserve refunded because the bidding
   * left this seat against bots only); payout = stake + delta − fee
   */
  seats: { seat: DdzSeat; stake: number; delta: number; fee: number; payout: number; vsBots: boolean }[];
  /** 摊牌: every hand at the end */
  hands: number[][];
  /** points of this hand by seat (Σ = 0; bots included): what a private table adds up; ±capped per pair */
  points: number[];
}

/** Header of a tournament table (sub-project 8): hands are played for points, without bets (as MjtTourneyView). */
export interface DdzTourneyView {
  tourneyId: string;
  title: string;
  stage: number;
  /** 1-based number of the current hand in the stage */
  handNo: number;
  /** hands in the stage */
  hands: number;
  /** stage points by seat (Σ = 0) */
  points: number[];
}

export interface DdzTableView {
  tableId: string;
  /**
   * 'tourney' — a tournament table (fixed seats, points, no bets; see `tourney`); 'training' — 练习: the player alone
   * with two bots on points (no stake, no Bet, no XP / achievements / leaderboards), leaving closes the table
   */
  kind: 'quick' | 'private' | 'tourney' | 'training';
  /** set on tournament tables (absent / null elsewhere) */
  tourney?: DdzTourneyView | null;
  tier: DdzTier;
  base: number;
  capX: number;
  feeBps: number;
  /** 'tokens' — quick play (pairs of humans play for tokens); 'points' — private / training table (no tokens, decision №34) */
  mode: 'tokens' | 'points';
  /** private / training table: running points by seat (Σ = 0); null on quick tables */
  points: number[] | null;
  /** private table: the host's seat (may fill the empty seats with bots) */
  hostSeat: DdzSeat | null;
  /** the own reserve of this hand (Bet.amount, placed with the deal); 0 = no tokens this hand (bots / private / salting) */
  myStake: number;
  /** private table code */
  code: string | null;
  handId: string | null;
  roundNo: number | null;
  dealNo: number;
  serverSeedHash: string | null;
  /** revealed after the hand */
  serverSeed: string | null;
  /** known once the salts are in */
  clientSeed: string | null;
  phase: DdzTablePhase;
  seq: number;
  /** always 3 */
  seats: DdzSeatView[];
  mySeat: DdzSeat | null;
  myHand: number[] | null;
  /** 底牌 once the landlord is known */
  bottom: number[] | null;
  turn: DdzSeat | null;
  lead: { seat: DdzSeat; play: DdzPlayedView } | null;
  /** for the player whose turn it is: may pass / has something that beats `lead` (false = 要不起) */
  canPass: boolean;
  canBeat: boolean | null;
  /** running multiple: bid × 2^(bombs + rocket), capped */
  multiple: number;
  /** every card played so far (记牌器) */
  played: number[];
  deadline: number | null;
  serverNow: number;
  result: DdzHandResult | null;
  nextHandAt: number | null;
}

export interface DdzQueueView {
  tier: DdzTier;
  queued: number;
  /** epoch ms when bots fill the table, null = not yet counting */
  botsAt: number | null;
  serverNow: number;
}

export interface DdzLobbyTier {
  tier: DdzTier;
  base: number;
  minBalance: number;
  /** landlord's max loss per hand = 2 × capX × base */
  maxLoss: number;
  queued: number;
  tables: number;
}
export interface DdzLobbyResponse {
  tiers: DdzLobbyTier[];
  capX: number;
  feeBps: number;
}

export interface DdzServerToClient {
  'ddz:table': (v: DdzTableView) => void;
  'ddz:queue': (v: DdzQueueView) => void;
}
export interface DdzClientToServer {
  'ddz:queue': (tier: DdzTier, ack: (r: DdzQueueView | DdzTableView | ApiError) => void) => void;
  'ddz:leave': (ack: (r: { ok: true } | ApiError) => void) => void;
  'ddz:private:create': (tier: DdzTier, ack: (r: DdzTableView | ApiError) => void) => void;
  'ddz:private:join': (code: string, ack: (r: DdzTableView | ApiError) => void) => void;
  /** host only: fill empty seats with bots */
  'ddz:private:bots': (ack: (r: DdzTableView | ApiError) => void) => void;
  /** 练习: a training table — the player and two bots on points, dealt at once (seated already → that table) */
  'ddz:training': (ack: (r: DdzTableView | ApiError) => void) => void;
  'ddz:salt': (input: { handId: string; salt: string }) => void;
  'ddz:action': (input: DdzActionRequest, ack: (r: DdzTableView | ApiError) => void) => void;
  /** 托管 on / off; `reason: 'leave'` — the page switched it on when the player left it (not carried to the next
   *  tournament stage, as `mjt:auto`) */
  'ddz:auto': (on: boolean, reason?: 'leave') => void;
  /** the table page is open on this socket / was left (presence: 托管 when the last one goes) */
  'ddz:present': (on: boolean) => void;
  /** after a reconnect: my table, my queue or nothing */
  'ddz:resume': (ack: (r: DdzTableView | DdzQueueView | null | ApiError) => void) => void;
}
